import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, type Voucher } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';

const errorText = (e: unknown) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Das hat nicht geklappt.');
const day = (iso: string | null) => (iso ? iso.split('-').reverse().join('.') : '–');
const valueText = (v: Pick<Voucher, 'kind' | 'value'>) => (v.kind === 'percent' ? `${v.value} %` : formatCents(v.value));

/**
 * Rabattcodes für die Fahrgast-App (KON-263, bis 30.10.2026 „Gutscheine“). Ein Rabattcode hat einen Code, einen Nachlass in Prozent oder als Betrag, einen Gültigkeitszeitraum, eine Höchstzahl an Einlösungen und eine Grenze je
 * Person. Der Fahrgast gibt den Code in der App bei der Buchung ein. Ein Gutschein mit Guthaben ist etwas anderes, siehe GiftVouchersPage. Der Nachlass steht dann in der Anfrage der Fahrt (Gutscheincode und Text), Sie berücksichtigen ihn beim Auftrag und der Rechnung.
 * Mit der Stornierung der Fahrt wird die Einlösung wieder frei.
 */
export function DiscountCodesPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const list = useQuery({ queryKey: ['discount-codes'], queryFn: async () => (await api<{ data: Voucher[] }>('GET', '/discount-codes')).data });
  const [form, setForm] = useState({ code: '', kind: 'percent' as 'percent' | 'amount', value: '', validFrom: '', validUntil: '', maxUses: '', perPerson: '1', note: '' });
  const [error, setError] = useState<string | null>(null);
  const write = can('vouchers.write');

  const create = useMutation({
    mutationFn: async () => {
      const raw = Number(form.value.replace(',', '.'));
      if (!Number.isFinite(raw) || raw <= 0) throw new Error('Bitte geben Sie den Wert an.');
      const value = form.kind === 'percent' ? Math.round(raw) : Math.round(raw * 100);
      return api('POST', '/discount-codes', {
        code: form.code.trim(),
        kind: form.kind,
        value,
        validFrom: form.validFrom || null,
        validUntil: form.validUntil || null,
        maxUses: form.maxUses ? Number(form.maxUses) : null,
        perPerson: Number(form.perPerson) || 1,
        note: form.note.trim() || null,
      });
    },
    onSuccess: () => {
      setForm({ code: '', kind: 'percent', value: '', validFrom: '', validUntil: '', maxUses: '', perPerson: '1', note: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['discount-codes'] });
    },
    onError: (e) => setError(e instanceof Error && !(e instanceof ApiError) ? e.message : errorText(e)),
  });
  const toggle = useMutation({
    mutationFn: async (v: Voucher) => api('PATCH', `/discount-codes/${v.id}`, { active: !v.active }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['discount-codes'] }),
    onError: (e) => setError(errorText(e)),
  });

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    create.mutate();
  };

  return (
    <>
      <div className="page-head">
        <h1>Rabattcodes</h1>
      </div>
      <p className="muted">Ein Rabattcode ist ein fester Code für alle, zum Beispiel für eine Aktion: Jeder Fahrgast kann ihn in der Fahrgast-App bei der Buchung eingeben. Einen persönlichen Gutschein mit Guthaben stellen Sie unter „Gutscheine“ aus. Der Nachlass steht danach in der Anfrage der Fahrt, berücksichtigen Sie ihn beim Auftrag und in der Rechnung. Storniert der Fahrgast, wird die Einlösung wieder frei.</p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {write && (
        <form className="card" onSubmit={submit} noValidate>
          <h2>Neuer Rabattcode</h2>
          <div className="form-grid form-grid--aligned">
            <label className="field"><span>Code <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={form.code} onChange={set('code')} placeholder="WILLKOMMEN10" autoCapitalize="characters" required /></label>
            <label className="field">
              <span>Art</span>
              <select className="input" value={form.kind} onChange={set('kind')}>
                <option value="percent">Prozent</option>
                <option value="amount">Betrag in Euro</option>
              </select>
            </label>
            <label className="field"><span>{form.kind === 'percent' ? 'Nachlass in Prozent' : 'Nachlass in Euro'} <abbr title="Pflichtfeld">*</abbr></span><input className="input" inputMode="decimal" value={form.value} onChange={set('value')} required /></label>
            <label className="field"><span>Gültig ab</span><input className="input" type="date" value={form.validFrom} onChange={set('validFrom')} /></label>
            <label className="field"><span>Gültig bis</span><input className="input" type="date" value={form.validUntil} onChange={set('validUntil')} /></label>
            <label className="field"><span>Höchstzahl Einlösungen</span><input className="input" inputMode="numeric" value={form.maxUses} onChange={set('maxUses')} placeholder="leer: unbegrenzt" /></label>
            <label className="field"><span>Einlösungen je Person</span><input className="input" inputMode="numeric" value={form.perPerson} onChange={set('perPerson')} /></label>
            <label className="field field--wide"><span>Notiz (nur intern)</span><input className="input" value={form.note} onChange={set('note')} /></label>
          </div>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={create.isPending || !form.code.trim() || !form.value.trim()}>{create.isPending ? 'Wird angelegt …' : 'Rabattcode anlegen'}</button>
          </div>
        </form>
      )}

      <div className="card">
        <h2>Vorhandene Rabattcodes</h2>
        {list.isLoading && <p className="muted">Wird geladen …</p>}
        {list.isError && <div className="alert alert--error" role="alert">Die Rabattcodes konnten nicht geladen werden.</div>}
        {list.data && list.data.length === 0 && <p className="muted">Noch keine Rabattcodes.</p>}
        {list.data && list.data.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr><th>Code</th><th>Nachlass</th><th>Gültig</th><th className="num">Eingelöst</th><th>Stand</th><th /></tr>
              </thead>
              <tbody>
                {list.data.map((v) => (
                  <tr key={v.id}>
                    <td data-label="Code"><strong>{v.code}</strong>{v.note && <div className="muted small">{v.note}</div>}</td>
                    <td data-label="Nachlass">{valueText(v)}</td>
                    <td data-label="Gültig">{v.validFrom || v.validUntil ? `${day(v.validFrom)} bis ${day(v.validUntil)}` : 'unbegrenzt'}</td>
                    <td data-label="Eingelöst" className="num">{v.uses}{v.maxUses ? ` von ${v.maxUses}` : ''}<div className="muted small">{v.perPerson} je Person</div></td>
                    <td data-label="Stand"><span className={`badge ${v.active ? 'badge--won' : ''}`}>{v.active ? 'Aktiv' : 'Gesperrt'}</span></td>
                    <td>{write && <button type="button" className="btn btn--secondary btn--sm" onClick={() => toggle.mutate(v)} disabled={toggle.isPending}>{v.active ? 'Sperren' : 'Wieder aktivieren'}</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
